<script setup lang="ts">
import { computed } from 'vue'
import { buildKoraLegalHref, KORA_LEGAL_DOCUMENTS, koraLegalLinksLabels } from './legalLinks'
import type { KoraLegalDocument, KoraLegalNavigateEvent } from './types'

// KORA-189: the footer links every product shows to the Terms, the privacy notice and the imprint.
// They are plain links to Kora Web's public pages, so they work without a sign-in and open in a new
// tab on request. The imprint is therefore one click from every page (§ 5 DDG: at most two).
const props = defineProps<{
  /** Kora Web's origin, or null when the host is Kora Web itself. */
  koraWebOrigin: string | null
  /** The effective language of the host ('en' | 'de'; anything else reads as English). */
  locale: string
}>()
const emit = defineEmits<{ navigate: [event: KoraLegalNavigateEvent] }>()

const labels = computed(() => koraLegalLinksLabels(props.locale))
const links = computed(() =>
  KORA_LEGAL_DOCUMENTS.map(document => ({
    document,
    label: labels.value[document],
    href: buildKoraLegalHref(document, props.koraWebOrigin, props.locale)
  }))
)

function onClick(event: MouseEvent, document: KoraLegalDocument, href: string): void {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  emit('navigate', { document, href, preventDefault: () => event.preventDefault() })
}
</script>

<template>
  <nav class="kora-legal-links" :aria-label="labels.navLabel" data-testid="kora-legal-links">
    <ul class="kora-legal-links__list">
      <li v-for="link in links" :key="link.document">
        <a
          class="kora-legal-links__link"
          :href="link.href"
          :data-legal="link.document"
          @click="onClick($event, link.document, link.href)"
          >{{ link.label }}</a
        >
      </li>
    </ul>
  </nav>
</template>

<style scoped>
.kora-legal-links {
  font-family: var(
    --kora-menu-font-family,
    'Public Sans Variable',
    'Public Sans',
    system-ui,
    -apple-system,
    sans-serif
  );
  font-size: var(--kora-menu-font-size, 0.875rem);
}
.kora-legal-links__list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1.25rem;
  list-style: none;
  margin: 0;
  padding: 0;
}
.kora-legal-links__link {
  color: var(--kora-menu-text-muted, #667085);
  text-decoration: underline;
  border-radius: 2px;
}
.kora-legal-links__link:hover {
  color: var(--kora-menu-text, #101828);
}
.kora-legal-links__link:focus-visible {
  outline: 2px solid var(--kora-menu-focus-ring, #3b5ba9);
  outline-offset: 2px;
}
</style>
